import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";
import { Avatar, Box, Divider, Menu, MenuItem, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useNavigate } from "react-router-dom";

import SchoolAuthContext from "../../context/SchoolAuthContext";
import SchoolSidebar from "./SchoolSidebar";

const DASHBOARD_SCALE = 0.75;

const getNestedValue = (obj, paths, fallback = "") => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, key) => acc?.[key], obj);

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const SchoolDashboardLayout = ({
  children,
  title = "Welcome to School Dashboard",
}) => {
  const navigate = useNavigate();
  const { schoolUser, logoutSchool } = useContext(SchoolAuthContext);
  const [profileAnchor, setProfileAnchor] = useState(null);

  const school = useMemo(() => schoolUser?.school || {}, [schoolUser]);
  const userInfo = useMemo(() => schoolUser?.userInfo || {}, [schoolUser]);
  const schoolName = getNestedValue(
    school,
    ["school_name", "name"],
    getNestedValue(userInfo, ["name"], "School"),
  );
  const schoolEmail = getNestedValue(
    school,
    ["email", "school_gmail_id"],
    getNestedValue(userInfo, ["email"], ""),
  );

  const handleLogout = () => {
    setProfileAnchor(null);
    logoutSchool();
    navigate("/school-portal/login");
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#FAFAFC",
      }}
    >
      <Box
        sx={{
          width: `calc(100vw / ${DASHBOARD_SCALE})`,
          height: `calc(100vh / ${DASHBOARD_SCALE})`,
          transform: `scale(${DASHBOARD_SCALE})`,
          transformOrigin: "top left",
          display: "flex",
          bgcolor: "#FAFAFC",
          fontFamily: '"Inter", "Poppins", sans-serif',
        }}
      >
        <SchoolSidebar onLogout={handleLogout} />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            overflow: "auto",
            px: 3,
            py: 3.25,
            bgcolor: "#FAFAFC",
          }}
        >
          <Box
            sx={{
              minHeight: 58,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: "1px solid #EFEFF4",
              mb: 3,
              pb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#171725" }}>
              {title}
            </Typography>

            <Box
              component="button"
              type="button"
              onClick={(event) => setProfileAnchor(event.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: "1px solid #EFEFF4",
                bgcolor: "#fff",
                borderRadius: "10px",
                px: 1.2,
                py: 0.8,
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
                "&:hover": { bgcolor: "#F8F9FC" },
              }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "#2D315F",
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                {schoolName?.[0]?.toUpperCase()}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#171725" }}>
                  {schoolName}
                </Typography>

                <Typography sx={{ fontSize: 13, color: "#92929D" }}>
                  School
                </Typography>
              </Box>

              <KeyboardArrowDownIcon sx={{ color: "#111827", fontSize: 20 }} />
            </Box>

            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={() => setProfileAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 240,
                  borderRadius: "10px",
                  border: "1px solid #EFEFF4",
                  boxShadow: "0 18px 48px rgba(45, 49, 95, 0.16)",
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.4 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#171725" }}>
                  {schoolName}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#92929D", mt: 0.3 }}>
                  {schoolEmail}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  minHeight: 42,
                  gap: 1,
                  color: "#B42318",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>

          {children}
        </Box>
      </Box>
    </Box>
  );
};

SchoolDashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
};

export default SchoolDashboardLayout;
